import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SignaturePad } from '../components/SignaturePad.jsx';
import { Centered } from './NotFound.jsx';

const BASE = '/api';
const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC','PR'];

const BIZ_LABELS = {
  business_name: 'Business name', dba: 'DBA', entity_type: 'Entity type',
  industry: 'Industry', business_start_date: 'Business start date',
  state_of_incorporation: 'State of incorporation',
  business_address_street: 'Street', business_address_line2: 'Suite / unit',
  business_city: 'City', business_state: 'State', business_zip: 'ZIP',
  amount_requested: 'Amount requested', gross_monthly_sales: 'Gross monthly sales',
  avg_bank_balance: 'Avg bank balance', monthly_cc_volume: 'Monthly card volume',
};

function validate(draft) {
  const errors = {};
  if (!draft.first_name?.trim()) errors.first_name = 'Required.';
  if (!draft.last_name?.trim())  errors.last_name  = 'Required.';
  const pct = Number(draft.ownership_pct);
  if (!pct || pct <= 0 || pct > 100) errors.ownership_pct = 'Must be 1–100.';
  const ssnDigits = (draft.ssn || '').replace(/\D/g, '');
  if (ssnDigits.length !== 9) errors.ssn = 'Must be 9 digits.';
  if (!draft.dob) {
    errors.dob = 'Required.';
  } else {
    const dob = new Date(draft.dob);
    if (isNaN(dob.getTime())) errors.dob = 'Invalid date.';
    else {
      const age = (Date.now() - dob.getTime()) / (365.25 * 24 * 3600 * 1000);
      if (age < 18) errors.dob = 'Owner must be at least 18.';
      else if (age > 110) errors.dob = 'Date looks incorrect.';
    }
  }
  if (!draft.address_line1?.trim()) errors.address_line1 = 'Required.';
  if (!draft.city?.trim())          errors.city          = 'Required.';
  if (!draft.state?.trim())         errors.state         = 'Required.';
  const zip = (draft.zip || '').replace(/\D/g, '').slice(0, 5);
  if (!/^\d{5}$/.test(zip))         errors.zip           = 'Must be 5 digits.';
  return errors;
}

export function Signer() {
  const { signerToken } = useParams();
  const [state, setState] = useState({ phase: 'loading' });
  const [draft, setDraft] = useState(null);
  const [errors, setErrors] = useState({});
  const [signature, setSignature] = useState(null);
  const [consentEsign, setConsentEsign] = useState(false);
  const [consentCredit, setConsentCredit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    (async () => {
      const res = await fetch(`${BASE}/sign/${signerToken}`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setState({ phase: 'error', status: res.status, error: body.error });
        return;
      }
      if (body.already_signed) {
        setState({ phase: 'done', businessName: body.package.business_name });
        return;
      }
      const o = body.owner || {};
      setDraft({
        first_name: o.first_name || '', last_name: o.last_name || '',
        ownership_pct: o.ownership_pct ?? '', email: o.email || '', phone: o.phone || '',
        ssn: '', dob: '',
        address_line1: o.address_line1 || '', address_line2: o.address_line2 || '',
        city: o.city || '', state: o.state || '', zip: o.zip || '',
      });
      setState({ phase: 'form', pkg: body.package, business: body.business || {}, owner: o });
    })().catch(() => setState({ phase: 'error', status: 0 }));
  }, [signerToken]);

  function patch(k, v) {
    setDraft((d) => ({ ...d, [k]: v }));
    if (errors[k]) setErrors((e) => { const { [k]: _, ...rest } = e; return rest; });
  }

  async function submit() {
    setSubmitError(null);
    const errs = validate(draft);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    if (!signature || !consentEsign || !consentCredit) return;
    setBusy(true);
    try {
      const res = await fetch(`${BASE}/sign/${signerToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...draft,
          ownership_pct: Number(draft.ownership_pct),
          signature_svg: signature.svg,
          signature_png_b64: signature.png_b64,
          consent_esign: true,
          consent_credit: true,
          idempotency_key: crypto.randomUUID(),
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || body.error || 'Something went wrong.');
      setState((s) => ({ phase: 'done', businessName: s.pkg?.business_name }));
    } catch (e) {
      setSubmitError(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (state.phase === 'loading') {
    return <Centered title="Loading…"><p>Preparing your section of the application.</p></Centered>;
  }
  if (state.phase === 'error') {
    if (state.status === 409) {
      return <Centered title="Not quite yet">
        <p>The other owner has not finished their part of the application. You will receive a fresh link when it is your turn to sign.</p>
      </Centered>;
    }
    if (state.status === 410) {
      return <Centered title="This link is no longer active">
        <p>Please contact your Fundmerica representative for a new link.</p>
      </Centered>;
    }
    return <Centered title="Link not found"><p>This signing link could not be opened.</p></Centered>;
  }
  if (state.phase === 'done') {
    return <Centered title="Thank you — you're all set">
      <p>Your section of the application{state.businessName ? ` for ${state.businessName}` : ''} has been completed and signed.</p>
    </Centered>;
  }

  const bizEntries = Object.entries(BIZ_LABELS)
    .map(([k, label]) => [label, state.business[k]])
    .filter(([, v]) => v !== undefined && v !== null && v !== '');
  const canSign = !!signature && consentEsign && consentCredit;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <img src="/fundmerica-logo.png" alt="Fundmerica" className="h-8 sm:h-10 w-auto" />
          <div className="text-xs text-slate-500 shrink-0">Owner section &amp; signature</div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        <header>
          <h2 className="text-xl font-semibold text-brand-900">
            Hi {state.owner.first_name || 'there'} — complete your owner section
          </h2>
          <p className="text-sm text-slate-500">
            You were listed as an owner of {state.pkg.business_name || 'this business'} on a Fundmerica funding
            application. Review the business information, fill in your own details, and sign below.
          </p>
        </header>

        {bizEntries.length > 0 && (
          <div className="card p-4">
            <h3 className="font-medium text-slate-800 mb-3">Business information (read only)</h3>
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
              {bizEntries.map(([label, v]) => (
                <div key={label} className="flex justify-between gap-3 border-b border-slate-100 py-1">
                  <dt className="text-slate-500">{label}</dt>
                  <dd className="text-slate-800 text-right">{String(v)}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        <div className="card p-4 space-y-4">
          <h3 className="font-medium text-slate-800">Your information</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="First name" req error={errors.first_name} v={draft.first_name} on={(v) => patch('first_name', v)} />
            <Field label="Last name"  req error={errors.last_name}  v={draft.last_name}  on={(v) => patch('last_name', v)} />
            <Field label="Ownership %" req type="number" error={errors.ownership_pct} v={draft.ownership_pct} on={(v) => patch('ownership_pct', v)} />
            <Field label="Date of birth" req type="date" error={errors.dob} v={draft.dob} on={(v) => patch('dob', v)} />
            <Field label="SSN" req placeholder="XXX-XX-XXXX" error={errors.ssn} v={draft.ssn} on={(v) => patch('ssn', v)} />
            <Field label="Mobile phone" type="tel" v={draft.phone} on={(v) => patch('phone', v)} />
            <Field label="Email" type="email" v={draft.email} on={(v) => patch('email', v)} />
            <div />
            <Field label="Home street" req error={errors.address_line1} v={draft.address_line1} on={(v) => patch('address_line1', v)} />
            <Field label="Suite / unit" v={draft.address_line2} on={(v) => patch('address_line2', v)} />
            <Field label="City" req error={errors.city} v={draft.city} on={(v) => patch('city', v)} />
            <Select label="State" req error={errors.state} v={draft.state} on={(v) => patch('state', v)} options={STATES} />
            <Field label="ZIP" req error={errors.zip} v={draft.zip} on={(v) => patch('zip', v)} />
          </div>
        </div>

        <div className="card p-4">
          <SignaturePad onChange={setSignature} />
        </div>

        <div className="card p-4 space-y-3 text-sm">
          <label className="flex items-start gap-3">
            <input type="checkbox" className="mt-1" checked={consentEsign} onChange={(e) => setConsentEsign(e.target.checked)} />
            <span>I agree to sign this application electronically under the E-SIGN Act, and I consent to receive related disclosures electronically.</span>
          </label>
          <label className="flex items-start gap-3">
            <input type="checkbox" className="mt-1" checked={consentCredit} onChange={(e) => setConsentCredit(e.target.checked)} />
            <span>I authorize Fundmerica and its funding partners to obtain consumer and business credit reports for the purpose of evaluating this application.</span>
          </label>
        </div>

        {submitError && <p className="field-error">{submitError}</p>}

        <div className="flex justify-end">
          <button className="btn-primary" onClick={submit} disabled={!canSign || busy}>
            {busy ? 'Submitting…' : 'Sign and submit my section'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, type = 'text', v, on, req, error, placeholder }) {
  return (
    <label className="block">
      <span className="field-label">{label}{req && <span className="text-red-500"> *</span>}</span>
      <input
        type={type}
        className={`field-input ${error ? 'is-error' : ''}`}
        aria-invalid={!!error}
        value={v ?? ''}
        placeholder={placeholder}
        onChange={(e) => on(e.target.value)}
      />
      {error && <p className="field-error">{error}</p>}
    </label>
  );
}

function Select({ label, v, on, options, req, error }) {
  return (
    <label className="block">
      <span className="field-label">{label}{req && <span className="text-red-500"> *</span>}</span>
      <select
        className={`field-select ${error ? 'is-error' : ''}`}
        aria-invalid={!!error}
        value={v ?? ''}
        onChange={(e) => on(e.target.value)}
      >
        <option value="">— select —</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      {error && <p className="field-error">{error}</p>}
    </label>
  );
}
